import Cookies from "js-cookie";
import { useCashInputStore } from "@/store/cash-input.store";
import { useCashierStore } from "@/store/cashier.store";
import { useLayoutStore, useSessionStore } from "@/store";
import { useOrderPaymentMethodStore } from "@/store/order-payment-method.store";
import { useProductStore } from "@/store/product.store";
import { useSaleStore } from "@/store/sale.store";
import { useShoppingCartStore } from "@/store/shopping-cart.store";
import { usePendingSalesStore } from "@/store/pending-sales.store";
import {
  removeAuthHeader,
  removeCashierIdHeader,
  removeStoreIdHeader,
} from "@/interceptors";
import { hasPendingOfflineSalesInStore } from "@/utilities/sync-session.utils";

const POS_PERSIST_STORES = [
  useCashierStore.persist,
  useCashInputStore.persist,
  useShoppingCartStore.persist,
  usePendingSalesStore.persist,
  useOrderPaymentMethodStore.persist,
] as const;

const ALL_PERSIST_STORES = [
  ...POS_PERSIST_STORES,
  useLayoutStore.persist,
  useProductStore.persist,
  useSaleStore.persist,
  useSessionStore.persist,
] as const;

async function clearPersistedStores(
  stores: readonly { clearStorage: () => void | Promise<void> }[]
): Promise<void> {
  await Promise.all(stores.map((store) => Promise.resolve(store.clearStorage())));
}

function resetPosStoresInMemory(): void {
  useCashierStore.getState().resetStore();
  useCashierStore.getState().setCashierId(undefined);
  useCashInputStore.getState().resetStore();
  useShoppingCartStore.getState().resetStore();
  usePendingSalesStore.getState().resetStore();
  useOrderPaymentMethodStore.getState().resetStore();
  useSaleStore.getState().resetStore();

  const session = useSessionStore.getState();
  session.setStoreId(undefined);
  session.setCurrentItem(undefined);
  session.setCurrentCustomer(undefined);
  session.setCustomerAddInfo(null);
  session.setIsCurrentCustomerDefaultCustomer(true);
}

export type ClearStationOptions = {
  /** Mantiene token y datos de empresa; solo limpia caja, carrito y turno. */
  preserveAuth?: boolean;
};

/**
 * Limpia estado local de caja/POS (IndexedDB + memoria).
 * Usar al cambiar de turno, detectar CAJA_CERRADA o antes de un login nuevo.
 */
/** Limpia turno/caja local al mostrar la pantalla de login (sin borrar token si existe). */
export async function prepareForLogin(): Promise<void> {
  await clearStationState({ preserveAuth: true });
  useSessionStore.getState().setAccount(undefined);
}

export async function clearStationState(
  options: ClearStationOptions = {}
): Promise<void> {
  const { preserveAuth = false } = options;

  if (!hasPendingOfflineSalesInStore()) {
    Cookies.remove("cashierId");
    removeCashierIdHeader();
  }
  Cookies.remove("storeId");
  removeStoreIdHeader();

  if (preserveAuth) {
    await clearPersistedStores(POS_PERSIST_STORES);
    resetPosStoresInMemory();
    return;
  }

  Cookies.remove("authToken");
  Cookies.remove("companyTaxId");
  removeAuthHeader();

  await clearPersistedStores(ALL_PERSIST_STORES);

  useCashInputStore.getState().resetStore();
  useCashierStore.getState().resetStore();
  useLayoutStore.getState().resetStore();
  useOrderPaymentMethodStore.getState().resetStore();
  useProductStore.getState().resetStore();
  useSaleStore.getState().resetStore();
  useSessionStore.getState().resetStore();
  useShoppingCartStore.getState().resetStore();
  usePendingSalesStore.getState().resetStore();
}
